import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";

const OUTCOME_MEASURES = [
  {
    id: "copm",
    name: "COPM",
    fullName: "Canadian Occupational Performance Measure",
    domain: "Occupational performance & satisfaction",
    scoring: "Performance 1–10, Satisfaction 1–10 (mean of top 5 problems)",
    mcid: "≥2 points on performance or satisfaction",
    timeToComplete: "20–40 min",
    format: "Semi-structured interview",
    use: "Core OT outcome measure. Client-centred. Identifies what matters most to the patient.",
    conditions: ["RA", "SLE", "SSc", "axSpA", "OA", "PsA"],
    notes: "Must be administered by trained OT. Not self-complete. Validated in rheumatology (Law et al., 1994; Goldsmith et al., 2000).",
  },
  {
    id: "dash",
    name: "DASH",
    fullName: "Disabilities of the Arm, Shoulder and Hand",
    domain: "Upper limb function",
    scoring: "30-item questionnaire. Score 0–100 (0 = no disability, 100 = severe disability). Formula: (sum of items / n – 1) × 25",
    mcid: "MCID ≈ 10–15 points",
    timeToComplete: "5–10 min",
    format: "Self-complete questionnaire",
    use: "Widely used in rheumatology OT. Captures physical, social, and symptom dimensions of upper limb disability.",
    conditions: ["RA", "SSc", "PsA", "CTD"],
    notes: "QuickDASH is an 11-item abbreviated version (MCID ≈ 16 points). Freely available from DASH Outcome Measure website.",
  },
  {
    id: "haq",
    name: "HAQ-DI",
    fullName: "Health Assessment Questionnaire — Disability Index",
    domain: "Functional disability across 8 ADL categories",
    scoring: "8 categories (dressing, rising, eating, walking, hygiene, reach, grip, activities). Score 0–3 per item. HAQ-DI = mean of highest score per category. Range 0–3.",
    mcid: "MCID ≈ 0.22 in RA",
    timeToComplete: "5–10 min",
    format: "Self-complete questionnaire",
    use: "Standard RA functional outcome measure. Used in DAS28 composite assessments and NICE criteria monitoring. Also valid in SLE, SSc, PsA.",
    conditions: ["RA", "SLE", "SSc", "PsA"],
    notes: "Freely available. HAQ-DI ≥ 1.5 indicates moderate–severe functional impairment. Floor and ceiling effects exist.",
  },
  {
    id: "abilhand",
    name: "ABILHAND",
    fullName: "ABILHAND",
    domain: "Manual ability — bimanual activities",
    scoring: "22 activities rated: Impossible (0), Difficult (1), Easy (2). Rasch analysis gives a linear measure in logits. Higher = better manual ability.",
    mcid: "Not well-established; use clinical context",
    timeToComplete: "5–10 min",
    format: "Interview or self-complete",
    use: "Captures real-world bimanual ability. Particularly useful in RA, SSc, inflammatory myopathy where grip alone does not capture full hand function.",
    conditions: ["RA", "SSc", "Inflammatory myopathy"],
    notes: "Freely available from UCLouvain (www.rehab-scales.org). Rasch scoring software required for precise measurement — clinical use often uses raw score.",
  },
  {
    id: "eq5d",
    name: "EQ-5D",
    fullName: "EuroQol 5-Dimension",
    domain: "Health-related quality of life (HRQoL)",
    scoring: "5 dimensions (mobility, self-care, usual activities, pain/discomfort, anxiety/depression). Each rated 1–5. EQ-VAS 0–100. EQ-5D-5L index value from UK value set.",
    mcid: "MCID ≈ 0.05–0.07 on EQ-5D index",
    timeToComplete: "2–3 min",
    format: "Self-complete questionnaire",
    use: "Used for health economic analysis and cross-condition comparisons. Required by NICE for Technology Appraisals. Less OT-specific but useful for holistic baseline.",
    conditions: ["All"],
    notes: "EQ-5D-5L preferred over EQ-5D-3L. Requires license for use (free for NHS/HEI). Available from EuroQol Foundation.",
  },
  {
    id: "aims2",
    name: "AIMS2",
    fullName: "Arthritis Impact Measurement Scales 2",
    domain: "Multi-dimensional impact of arthritis",
    scoring: "57 items across 12 scales (mobility, walking/bending, hand/finger function, self-care, household tasks, social activities, support from family, work, pain, mood, satisfaction). Standardised scores per scale 0–10.",
    mcid: "Variable by scale",
    timeToComplete: "15–20 min",
    format: "Self-complete questionnaire",
    use: "Comprehensive arthritis-specific QoL measure. Captures physical, social, and psychological dimensions. Useful for complex, high-impact patients.",
    conditions: ["RA", "OA", "PsA"],
    notes: "Longer than most — consider burden in fatigued patients. AIMS2-SF is a shorter version (26 items). Freely available.",
  },
];

function CopmlCalculator() {
  const [scores, setScores] = useState<{ perf: string; sat: string }[]>(
    Array(5).fill({ perf: "", sat: "" })
  );

  const update = (i: number, field: "perf" | "sat", value: string) => {
    setScores((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  };

  const validPerf = scores.map((s) => parseFloat(s.perf)).filter((v) => !isNaN(v) && v >= 1 && v <= 10);
  const validSat = scores.map((s) => parseFloat(s.sat)).filter((v) => !isNaN(v) && v >= 1 && v <= 10);

  const perfScore = validPerf.length > 0 ? (validPerf.reduce((a, b) => a + b, 0) / validPerf.length).toFixed(1) : null;
  const satScore = validSat.length > 0 ? (validSat.reduce((a, b) => a + b, 0) / validSat.length).toFixed(1) : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Enter performance (1–10) and satisfaction (1–10) for up to 5 priority occupational problems identified by the patient.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 w-8">#</th>
              <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500">Problem</th>
              <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500">Performance (1–10)</th>
              <th className="text-left py-2 text-xs font-semibold text-slate-500">Satisfaction (1–10)</th>
            </tr>
          </thead>
          <tbody>
            {scores.map((s, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2 pr-4 text-slate-400 font-medium">{i + 1}</td>
                <td className="py-2 pr-4 text-slate-400 italic text-xs">Problem {i + 1}</td>
                <td className="py-2 pr-4">
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={s.perf}
                    onChange={(e) => update(i, "perf", e.target.value)}
                    className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="1–10"
                  />
                </td>
                <td className="py-2">
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={s.sat}
                    onChange={(e) => update(i, "sat", e.target.value)}
                    className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="1–10"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(perfScore || satScore) && (
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-amber-700">{perfScore ?? "–"}</p>
            <p className="text-sm text-amber-600 mt-1">Performance Score</p>
            <p className="text-xs text-amber-500 mt-0.5">MCID = ≥2 points change</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-orange-700">{satScore ?? "–"}</p>
            <p className="text-sm text-orange-600 mt-1">Satisfaction Score</p>
            <p className="text-xs text-orange-500 mt-0.5">MCID = ≥2 points change</p>
          </div>
        </div>
      )}
    </div>
  );
}

function DashCalculator() {
  const [items, setItems] = useState<string[]>(Array(30).fill(""));
  const [useQuick, setUseQuick] = useState(false);

  const count = useQuick ? 11 : 30;
  const validItems = items.slice(0, count).map((v) => parseFloat(v)).filter((v) => !isNaN(v) && v >= 1 && v <= 5);
  const score = validItems.length === count
    ? (((validItems.reduce((a, b) => a + b, 0) / count) - 1) * 25).toFixed(1)
    : null;

  const interpretation = score !== null
    ? parseFloat(score) < 20 ? "Mild disability"
      : parseFloat(score) < 50 ? "Moderate disability"
        : "Severe disability"
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <button
          onClick={() => setUseQuick(false)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!useQuick ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
        >
          DASH (30 items)
        </button>
        <button
          onClick={() => setUseQuick(true)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${useQuick ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
        >
          QuickDASH (11 items)
        </button>
      </div>
      <p className="text-sm text-slate-600">Rate each item 1–5 (1 = no difficulty, 5 = unable). Enter scores below.</p>
      <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
        {Array(count).fill(0).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <label className="text-xs text-slate-400 text-center">Q{i + 1}</label>
            <input
              type="number"
              min="1"
              max="5"
              value={items[i]}
              onChange={(e) => setItems((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="1–5"
            />
          </div>
        ))}
      </div>
      {score !== null && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <p className="text-4xl font-bold text-amber-700">{score}</p>
          <p className="text-sm text-amber-600 mt-1">{useQuick ? "QuickDASH" : "DASH"} Score (0–100)</p>
          <p className="text-sm text-amber-800 font-medium mt-1">{interpretation}</p>
          <p className="text-xs text-amber-500 mt-0.5">MCID ≈ {useQuick ? "16" : "10–15"} points</p>
        </div>
      )}
    </div>
  );
}

export default function OutcomeMeasures() {
  const [selectedMeasure, setSelectedMeasure] = useState<string | null>(null);

  return (
    <OTLayout title="Outcome Measures">
      <div className="space-y-8">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>RCOT guidance:</strong> Outcome measures should be selected to match the intervention goal and be meaningful to the patient. Use the COPM as the primary OT-specific outcome measure. Supplement with condition-specific measures as appropriate.
        </div>

        {/* Interactive Calculators */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Interactive Calculators</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setSelectedMeasure(selectedMeasure === "copm" ? null : "copm")}
              className={`bg-white rounded-2xl border p-5 text-left transition-all ${selectedMeasure === "copm" ? "border-amber-400 shadow-md" : "border-slate-200 hover:border-amber-300"}`}
            >
              <h3 className="font-bold text-slate-900">COPM Calculator</h3>
              <p className="text-sm text-slate-500 mt-1">Performance & satisfaction score from 5 priority problems</p>
            </button>
            <button
              onClick={() => setSelectedMeasure(selectedMeasure === "dash" ? null : "dash")}
              className={`bg-white rounded-2xl border p-5 text-left transition-all ${selectedMeasure === "dash" ? "border-amber-400 shadow-md" : "border-slate-200 hover:border-amber-300"}`}
            >
              <h3 className="font-bold text-slate-900">DASH / QuickDASH Calculator</h3>
              <p className="text-sm text-slate-500 mt-1">Upper limb disability score (0–100)</p>
            </button>
          </div>

          {selectedMeasure === "copm" && (
            <div className="bg-white rounded-2xl border border-amber-300 p-6">
              <h3 className="font-bold text-slate-900 mb-4">COPM Score Calculator</h3>
              <CopmlCalculator />
            </div>
          )}
          {selectedMeasure === "dash" && (
            <div className="bg-white rounded-2xl border border-amber-300 p-6">
              <h3 className="font-bold text-slate-900 mb-4">DASH / QuickDASH Calculator</h3>
              <DashCalculator />
            </div>
          )}
        </section>

        {/* Reference Cards */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Outcome Measure Reference</h2>
          <div className="space-y-4">
            {OUTCOME_MEASURES.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-slate-900">{m.name} — {m.fullName}</h3>
                    <p className="text-sm text-amber-700 mt-0.5">{m.domain}</p>
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{m.timeToComplete}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Scoring</p>
                    <p className="text-slate-700">{m.scoring}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">MCID</p>
                    <p className="text-slate-700 font-medium text-amber-700">{m.mcid}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Format</p>
                    <p className="text-slate-700">{m.format}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600">{m.use}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {m.conditions.map((c) => (
                    <span key={c} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
                <p className="text-xs text-slate-400 italic">{m.notes}</p>
              </div>
            ))}
          </div>
        </section>

      </div>
    </OTLayout>
  );
}
